// @ts-nocheck
import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHotelPartnerId } from './hooks/useHotelPartnerId';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPropertiesFull, useDeleteHotelProperty, useSaveHotelProperty, useRfpRequests, useConvertRfpToProperty, HotelPropertyFull } from './hooks/useHotelManager';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Star, Upload, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';
import { format, parseISO, isPast } from 'date-fns';
import { HotelPropertyModal } from './HotelPropertyModal';
import { RfpFormModal } from './RfpFormModal';
import Papa from 'papaparse';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const HotelPropertiesManager: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { data: partnerId } = useHotelPartnerId();

  const { data: event } = useQuery({
    queryKey: ['event-detail', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, start_date, end_date')
        .eq('id', eventId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId && !!partnerId,
  });

  const { data: assignment } = useQuery({
    queryKey: ['hotel-assignment', eventId, partnerId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('id')
        .eq('event_id', eventId!)
        .eq('hotel_partner_user_id', partnerId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId && !!partnerId,
  });

  const { data: hotels, isLoading: hotelsLoading } = useHotelPropertiesFull(eventId);
  const { data: rfps, isLoading: rfpsLoading } = useRfpRequests(eventId);
  const deleteProperty = useDeleteHotelProperty(eventId);
  const saveProperty = useSaveHotelProperty(eventId);
  const convertRfp = useConvertRfpToProperty(eventId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState<HotelPropertyFull | null>(null);
  const [rfpModalOpen, setRfpModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProperty.mutateAsync(deleteTarget);
      toast.success('Hotel removed');
    } catch {
      toast.error('Failed to remove hotel');
    }
    setDeleteTarget(null);
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data as any[];
        let imported = 0;
        let errors = 0;

        for (const row of rows) {
          if (!row.hotel_name && !row['Hotel Name']) { errors++; continue; }
          try {
            await saveProperty.mutateAsync({
              data: {
                event_id: eventId,
                hotel_assignment_id: assignment?.id || null,
                hotel_name: row.hotel_name || row['Hotel Name'],
                address: row.address || row['Address'] || null,
                city: row.city || row['City'] || null,
                state: row.state || row['State'] || null,
                zip: row.zip || row['ZIP'] || null,
                distance_miles: parseFloat(row.distance_miles || row['Distance'] || '0') || null,
                group_rate_per_night: parseFloat(row.group_rate || row['Group Rate'] || '0') || null,
                booking_link: row.booking_link || row['Booking Link'] || null,
                rooms_contracted: parseInt(row.rooms_contracted || row['Rooms Contracted'] || '0') || null,
                rebate_per_room: parseFloat(row.rebate_per_room || row['Rebate'] || '0') || null,
                commission_pct: parseFloat(row.commission_pct || row['Commission'] || '0') || null,
                rfp_status: 'accepted',
              },
            });
            imported++;
          } catch {
            errors++;
          }
        }

        if (imported > 0) toast.success(`Imported ${imported} hotel(s)`);
        if (errors > 0) toast.error(`${errors} row(s) failed`);
      },
      error: () => toast.error('Failed to parse CSV'),
    });

    // Reset input
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const rfpStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'declined': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'expired': return <XCircle className="h-4 w-4 text-gray-400" />;
      default: return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const handleConvertRfp = async (rfp: any) => {
    try {
      const propertyId = await saveProperty.mutateAsync({
        data: {
          event_id: eventId,
          hotel_assignment_id: assignment?.id || null,
          hotel_name: rfp.hotel_name,
          address: rfp.hotel_address,
          city: rfp.hotel_city,
          state: rfp.hotel_state,
          rooms_contracted: rfp.rooms_requested,
          group_rate_per_night: rfp.offered_rate,
          hotel_contact_name: rfp.contact_name,
          hotel_contact_email: rfp.contact_email,
          hotel_contact_phone: rfp.contact_phone,
          rfp_status: 'accepted',
        },
      });
      await convertRfp.mutateAsync({ rfpId: rfp.id, propertyId });
      toast.success('RFP converted to hotel property');
    } catch {
      toast.error('Failed to convert RFP');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hotel-portal/hotels')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{event?.name || 'Hotels'}</h1>
          {event?.start_date && (
            <p className="text-sm text-muted-foreground">
              {format(parseISO(event.start_date), 'MMM d, yyyy')}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => { setEditingHotel(null); setModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Add Hotel
        </Button>
        <Button size="sm" variant="outline" onClick={() => csvInputRef.current?.click()}>
          <Upload className="h-4 w-4 mr-1" /> Import CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => setRfpModalOpen(true)}>
          <FileText className="h-4 w-4 mr-1" /> New RFP
        </Button>
        <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
      </div>

      {/* Hotel cards */}
      {hotelsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-56 rounded-lg" />
          ))}
        </div>
      ) : !hotels || hotels.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No hotels added yet. Add one manually or import from CSV.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hotels.map((hotel) => {
            const rateExpired = hotel.rate_valid_until && isPast(parseISO(hotel.rate_valid_until));
            const roomsContracted = hotel.rooms_contracted ?? 0;
            const pctBooked = roomsContracted > 0 ? Math.round((hotel.rooms_booked / roomsContracted) * 100) : 0;

            return (
              <Card key={hotel.id} className="flex flex-col">
                {hotel.photo_url && (
                  <div className="h-32 overflow-hidden rounded-t-lg">
                    <img src={hotel.photo_url} alt={hotel.hotel_name} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{hotel.hotel_name}</CardTitle>
                      {hotel.city && (
                        <p className="text-xs text-muted-foreground">{hotel.city}, {hotel.state}</p>
                      )}
                    </div>
                    {hotel.is_primary && (
                      <Badge variant="default" className="shrink-0">Primary</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 space-y-2 text-sm">
                  {hotel.star_rating != null && (
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.round(hotel.star_rating) }).map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  )}
                  {hotel.distance_miles != null && (
                    <p className="text-muted-foreground">{hotel.distance_miles} mi from venue</p>
                  )}
                  {hotel.group_rate_per_night != null && (
                    <p>
                      <span className="font-medium">${hotel.group_rate_per_night}</span>
                      <span className="text-muted-foreground">/night</span>
                      {rateExpired && (
                        <Badge variant="destructive" className="ml-2 text-xs">Rate Expired</Badge>
                      )}
                    </p>
                  )}
                  {roomsContracted > 0 && (
                    <p className="text-muted-foreground">
                      {hotel.rooms_booked}/{roomsContracted} rooms booked ({pctBooked}%)
                    </p>
                  )}
                  {hotel.amenities && hotel.amenities.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {hotel.amenities.slice(0, 4).map((a) => (
                        <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                      ))}
                      {hotel.amenities.length > 4 && (
                        <Badge variant="secondary" className="text-xs">+{hotel.amenities.length - 4}</Badge>
                      )}
                    </div>
                  )}
                  <p className="text-muted-foreground">Blocks: {hotel.block_rooms_held} held / {hotel.block_rooms_reserved} reserved</p>
                </CardContent>
                <div className="p-4 pt-0 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => { setEditingHotel(hotel); setModalOpen(true); }}
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget(hotel.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* RFP List */}
      {rfps && rfps.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">RFP Requests</h2>
          <div className="space-y-2">
            {rfps.map((rfp) => (
              <Card key={rfp.id}>
                <CardContent className="flex flex-col sm:flex-row sm:items-center gap-3 py-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {rfpStatusIcon(rfp.status)}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{rfp.hotel_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {rfp.rooms_requested ?? '—'} rooms · {rfp.desired_rate ? `$${rfp.desired_rate}/night` : 'Rate TBD'}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize shrink-0">{rfp.status}</Badge>
                  {rfp.status === 'sent' && !rfp.converted_to_property_id && (
                    <Button size="sm" variant="outline" onClick={() => handleConvertRfp(rfp)}>
                      Convert to Property
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {modalOpen && (
        <HotelPropertyModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); setEditingHotel(null); }}
          eventId={eventId!}
          hotelAssignmentId={assignment?.id || null}
          existing={editingHotel}
        />
      )}

      {rfpModalOpen && (
        <RfpFormModal
          open={rfpModalOpen}
          onClose={() => setRfpModalOpen(false)}
          eventId={eventId!}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Hotel</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the hotel from this event. Existing bookings referencing it will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HotelPropertiesManager;
